import React, { useState } from "react";
import { format } from "date-fns"; // Import the format function from date-fns
import { Formik, Form } from "formik";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Box,
  Autocomplete,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AddIcon from "@mui/icons-material/Add";
import ShareIcon from "@mui/icons-material/Share";
import * as Yup from "yup"; // For form validation
import "./information-sharing.css";
import ReusableDialog from "../../../../../common/dialog/dialog";
import Signature from "../../../../../common/signature/signature";

// Validation schema using Yup
const validationSchema = Yup.object({
  reason: Yup.string().required("reason is required"),
});

const roles = ["Social worker", "Staff team", "Responsible individual", "Principal Clinician", "Family"];

const InformationSharing = ({ updateFormData, prevStep, handleSubmit }) => {
  const initialValues = {
    reason: "",
  };

  const [open, setOpen] = useState(false); // For dialog
  const [records, setRecords] = useState([]); // Store records including default one
  const [dialogFormData, setDialogFormData] = useState({
    role: "",
    date: "",
    infoShared: "",
  }); // Dialog form data

  const [signatures, setSignatures] = useState([]); // Array to store all the signature refs

  const addSignature = () => {
    // Add a new signature with a unique id and ref
    setSignatures((prevSignatures) => [
      ...prevSignatures,
      { id: Date.now(), ref: React.createRef() }, // Create a new ref here
    ]);
  };

  const clearSignature = (id) => {
    // Clear the specific signature using its ref
    const signatureToClear = signatures.find((signature) => signature.id === id);
    signatureToClear?.ref.current?.clearSignature(); // Clear signature using the ref
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setDialogFormData({ role: "", date: "", infoShared: "" }); // Reset dialog form on close
  };

  const handleDialogInputChange = (e) => {
    const { name, value } = e.target;
    setDialogFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDialogSelectChange = (field, value) => {
    setDialogFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDialogSubmit = () => {
    // Add the dialog form data to records
    setRecords((prevRecords) => [...prevRecords, dialogFormData]);
    handleCloseDialog(); // Close the dialog after submission
  };

  // Function to format the date into "Wednesday, 29 November" format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, "EEEE, dd MMMM"); // Formats to "Wednesday, 29 November"
  };

  // Convert all signatures to base64
  const getAllSignaturesAsImages = () => {
    return signatures.map((signature) => {
      const signatureData = signature.ref.current?.getSignatureData(); // Assuming `getSignatureData` returns base64
      return signatureData; // Return base64 signature image data
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        // Convert all signatures to base64 before submitting
        const signaturesImages = getAllSignaturesAsImages();

        // Update form data with both dialog data and the main form data
        updateFormData({
          informationSharingInfo: {
            reason: values.reason,
            records,
            signatures: signaturesImages, // Send all signatures as base64 images
          },
        });
        handleSubmit(); // Final submit with all forms' data
      }}
      
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
        <Form onSubmit={handleSubmit} className='form-container'>
          <Typography variant='h6' sx={{ fontWeight: "bold" }} align='left'>
            Information Sharing
          </Typography>
          <Typography variant='body2' gutterBottom>
            Explain the reasons why this young person is being considered for placement.
          </Typography>

          {/* Dynamically render records (Social Worker Card and any other roles added) */}
          {records.length > 0 && (
            <Grid container spacing={2}>
              {records.map((record, index) => (
                <Grid item lg={6} xs={12} key={index}>
                  <Card className='social-worker-card'>
                    <CardContent>
                      <Grid container spacing={2} alignItems='center'>
                        <Grid item xs={10}>
                          <Typography variant='subtitle1' className='role-title'>
                            {record.role}
                          </Typography>
                          <Grid container spacing={1} alignItems='center'>
                            <Grid item>
                              <CalendarTodayIcon fontSize='small' />
                            </Grid>
                            <Grid item>
                              <Typography variant='body2'>
                                {formatDate(record.date)} {/* Display formatted date */}
                              </Typography>
                            </Grid>
                          </Grid>
                          <Grid container spacing={1} alignItems='center'>
                            <Grid item>
                              <ShareIcon fontSize='small' />
                            </Grid>
                            <Grid item>
                              <Typography variant='body2'>{record.infoShared}</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={2} align='right'>
                          <IconButton>
                            <MoreVertIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Add another role button */}
          <Button color='primary' onClick={handleOpenDialog} mt={2}>
            <AddIcon /> Add another record for another role
          </Button>

          {/* Dialog for Adding People */}
          <ReusableDialog
            isOpen={open}
            onClose={handleCloseDialog}
            title='Add another Record?'
            contentText='Add the relevant details to the fields to add another person to the involved parties.'
            onSubmit={handleDialogSubmit} // Submit to add a new card
            submitText='Save'
            cancelText='Cancel'
          >
            <Box mt={2}>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={5}>
                  <Autocomplete
                    id='role'
                    options={roles}
                    value={dialogFormData.role}
                    onChange={(event, newValue) => handleDialogSelectChange("role", newValue)}
                    renderInput={(params) => (
                      <>
                        <Typography sx={{ fontWeight: "bold" }} align='left'>
                          Role
                        </Typography>
                        <TextField {...params} placeholder='Role' />
                      </>
                    )}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={5}>
                  <Typography sx={{ fontWeight: "bold" }} align='left'>
                    Date
                  </Typography>
                  <TextField
                    fullWidth
                    name='date'
                    type='date'
                    InputLabelProps={{ shrink: true }}
                    value={dialogFormData.date}
                    onChange={handleDialogInputChange}
                  />
                </Grid>
              </Grid>

              {/* How the information was shared */}
              <Typography variant='body2' className='info-shared-text' mt={2}>
                How was the information shared
              </Typography>
              <RadioGroup
                aria-label='infoShared'
                name='infoShared'
                value={dialogFormData.infoShared}
                onChange={(e) => handleDialogInputChange(e)}
                row
              >
                <FormControlLabel value='email' control={<Radio />} label='By email' />
                <FormControlLabel value='faceToFace' control={<Radio />} label='Face to Face' />
                <FormControlLabel value='telephone' control={<Radio />} label='By telephone' />
              </RadioGroup>
            </Box>
          </ReusableDialog>

          {/* Conclusion on Assessment */}
          <Typography sx={{ fontWeight: "bold" }} align='left' mt={5}>
            Conclusion on assessment
          </Typography>
          <Typography variant='body2' className='conclusion-subtext'>
            Explain the reasons why this young person is being considered for placement.
          </Typography>

          {/* Rating Section */}
          <Grid container spacing={2} alignItems='center' className='rating-container'>
            <Grid item lg={2} md={2}>
              <Typography variant='subtitle1' className='role-title'>
                Overall
              </Typography>
              <Typography variant='h3' className='rating-percentage'>
                66%
              </Typography>
            </Grid>
            <Grid item lg={6} md={6}>
              <Typography variant='body2' className='rating-description'>
                This is not an acceptable match and the child should not be placed in this home currently.
              </Typography>
            </Grid>
          </Grid>

          {/* Text field for reasons */}
          <TextField
            id='reason'
            name='reason'
            placeholder='write Reasons for this rating...'
            fullWidth
            multiline
            rows={4}
            value={values.reason}
            onChange={handleChange}
            onBlur={handleBlur}
            error={Boolean(touched.reason && errors.reason)}
            helperText={touched.reason && errors.reason ? errors.reason : ""}
            variant='outlined'
          />

          {/* Render each signature dynamically in a 3-per-line grid */}
          <Grid container spacing={2}>
            {signatures.map((signature) => (
              <Grid item xs={12} sm={6} md={6} key={signature.id}>
                {/* 3 columns per row */}
                <Box className='signature-box' mt={2}>
                  <Signature ref={signature.ref} />
                  <Typography align='right'>
                    Parent/Guardian Signature
                    <Grid className='clear-signature-button'>
                      <Button variant='outlined' onClick={() => clearSignature(signature.id)}>
                        Clear
                      </Button>
                    </Grid>
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Add a signature button */}
          <Grid container justifyContent='space-between' alignItems='center' mt={4}>
            <Grid item></Grid>
            <Grid item>
              <Button color='primary' variant='contained' onClick={addSignature}>
                Add a signature
              </Button>
            </Grid>
          </Grid>

          {/* Navigation and Submit Buttons */}
          <Grid container justifyContent='space-between' alignItems='center' mt={2}>
            <Grid item>
              <Button type='button' variant='contained' color='primary' onClick={prevStep}>
                Previous
              </Button>
            </Grid>
            <Grid item>
              <Button type='submit' variant='contained' color='primary'>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default InformationSharing;
